import React from 'react';

/**
 * PLAT-11906 — put the Translation Review's read frontier inside one
 * translated fragment: the fragment's first `readChars` characters stay as
 * they are, and every character after them is wrapped in `Tail`, which draws
 * it as not yet read.
 *
 * The fragment's text may already carry marks (changed words, glossary terms,
 * word units). This only counts and splits text, copying every element it
 * passes through, so each mark keeps its place and its look on both sides of
 * the frontier.
 */
export function wrapUnreadTail(
  node: React.ReactNode,
  readChars: number,
  Tail: React.ElementType,
): React.ReactNode {
  let remaining = readChars;
  let tailCount = 0;
  const tail = (text: string) => (
    <Tail key={`unread-tail-${tailCount++}`}>{text}</Tail>
  );
  const walk = (current: React.ReactNode): React.ReactNode => {
    if (typeof current === 'string') {
      if (remaining >= current.length) {
        remaining -= current.length;
        return current;
      }
      const head = current.slice(0, Math.max(remaining, 0));
      remaining = 0;
      const rest = current.slice(head.length);
      return head ? [head, tail(rest)] : tail(rest);
    }
    if (Array.isArray(current)) return current.map(walk);
    if (React.isValidElement<{ children?: React.ReactNode }>(current)) {
      if (current.props.children === undefined) return current;
      return React.cloneElement(
        current,
        undefined,
        walk(current.props.children),
      );
    }
    return current;
  };
  return walk(node);
}
